'use client';

import { motion } from 'motion/react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { SegmentedControl } from '../../shared/segmented-control';
import { useSim } from './use-sim';

/**
 * A working miniature of the console's reliability tape: one row per model,
 * always sixty cells wide whatever the window, each cell coloured by the
 * worst thing that happened in it. Deterministic sample data.
 */

/**
 * Same three models, two real-feeling windows: zooming from 30 days to the
 * last hour changes which buckets are dirty while the 60-cell width holds,
 * which is the whole point of the fixed-width tape.
 */
const WINDOWS = {
  hour: [
    { name: 'video · flagship', eff: '99.7%', raw: '97.9%', bad: [44, 45] },
    { name: 'image · flagship', eff: '100%', raw: '99.4%', bad: [] },
    {
      name: 'video · legacy',
      eff: '96.8%',
      raw: '93.1%',
      bad: [11, 12, 13, 14, 15],
    },
  ],
  month: [
    { name: 'video · flagship', eff: '99.2%', raw: '96.1%', bad: [14, 15, 41] },
    { name: 'image · flagship', eff: '99.9%', raw: '98.8%', bad: [33] },
    {
      name: 'video · legacy',
      eff: '97.4%',
      raw: '94.0%',
      bad: [8, 9, 10, 27, 48, 49],
    },
  ],
} as const;

type TapeWindow = keyof typeof WINDOWS;
type TapeRow = (typeof WINDOWS)[TapeWindow][number];

function cellTone(row: TapeRow, i: number, seed: number) {
  if ((row.bad as readonly number[]).includes(i)) return 'bg-foreground';
  // Light deterministic texture so the tape reads alive, not flat.
  const wobble = Math.abs(Math.sin(i * 3.7 + row.bad.length + seed));
  return wobble > 0.92 ? 'bg-muted-foreground/60' : 'bg-muted-foreground/25';
}

export function ConsoleTape() {
  const [tapeWindow, setTapeWindow] = useState<TapeWindow>('month');
  const { ref, tick, reduceMotion } = useSim({ totalTicks: 1, msPerTick: 300 });
  const shown = reduceMotion || tick >= 1;
  const rows = WINDOWS[tapeWindow];
  const seed = tapeWindow === 'hour' ? 3 : 0;

  return (
    <div ref={ref} className="border border-edge bg-muted/20 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          reliability tape · one row per model
        </p>
        <SegmentedControl
          label="Tape window"
          labelHidden
          value={tapeWindow}
          onChange={setTapeWindow}
          options={[
            { value: 'hour', label: 'Last hour' },
            { value: 'month', label: 'Last 30 days' },
          ]}
        />
      </div>

      <div
        className="flex flex-col gap-2.5"
        role="img"
        aria-label="Miniature reliability tape: sixty cells per model row, dark cells mark buckets containing failures, with effective and raw success rates beside each row."
      >
        {rows.map((row, rowIndex) => (
          <div
            key={row.name}
            className="grid grid-cols-1 items-center gap-1.5 sm:grid-cols-[minmax(0,8rem)_1fr_auto] sm:gap-3"
          >
            <span className="truncate font-mono text-[10px] text-muted-foreground">
              {row.name}
            </span>
            <div className="flex h-4 items-stretch gap-px" aria-hidden>
              {Array.from({ length: 60 }, (_, i) => (
                <motion.span
                  key={`${tapeWindow}-${String(i)}`}
                  initial={reduceMotion ? false : { opacity: 0 }}
                  animate={{ opacity: shown ? 1 : 0 }}
                  transition={{
                    duration: 0.2,
                    delay: reduceMotion ? 0 : rowIndex * 0.15 + i * 0.008,
                  }}
                  className={cn('w-full', cellTone(row, i, seed))}
                />
              ))}
            </div>
            <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
              <b className="font-semibold text-foreground">{row.eff}</b> eff ·{' '}
              {row.raw} raw
            </span>
          </div>
        ))}
      </div>

      <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-edge pt-2 font-mono text-[10px] text-muted-foreground">
        <span>
          {tapeWindow === 'hour'
            ? '60 × 1-minute buckets'
            : '60 × 12-hour buckets'}
          , same width either way
        </span>
        <span>dark cell = the worst thing in that bucket</span>
        <span>&lt;95% warns · &lt;80% pages · min 20 outcomes</span>
      </p>
    </div>
  );
}
